import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, Typography, Chip } from '@mui/material';

import ContainerLoadingIndicator from 'src/components/common/loading/ContainerLoadingIndicator';
import NoDataView from 'src/components/common/modal/NoDataView';
import PageWithLayout from 'src/components/layout/PageWithLayout';
import { FabricImageAnnotator } from 'src/features/annotation/components/FabricImageAnnotator';
import ImageThumbnails from 'src/features/annotation/components/ImageThumbnails';
import StudyAssignmentActions from 'src/features/annotation/components/StudyAssignmentActions';
import { getStudyImageAnnotation } from 'src/features/annotation/store/annotationThunks';
import { IAnnotationLabelShortInfo } from 'src/features/annotation_label';
import {
	ANNOTATION_LABEL_LOADING_KEY,
	getAnnotationLabelsShortInfo,
} from 'src/features/annotation_label/store/annotationLabelThunk';
import QuestionnairePanel from 'src/features/questionnaire/components/QuestionnairePanel';
import { QUESTIONNAIRE_LOADING_KEY } from 'src/features/questionnaire/store';
import { QUESTIONNAIRE_TEMPLATE_LOADING_KEY } from 'src/features/questionnaire_template/store/questionnaireTemplateThunks';
import {
	IStudyAnnotation,
	IStudyImageAsset,
	IStudyWithImages,
} from 'src/features/study';
import {
	getStudyDetails,
	getStudyWithImages,
	STUDY_LOADING_KEY,
} from 'src/features/study/store/studyThunks';
import {
	IGetCurrentUsersStudyAssignmentResponse,
	IStudyWithAssignment,
	StudyAssignmentStatus,
} from 'src/features/study_assignment/domain/types';
import {
	getCurrentUsersStudyAssignment,
	getNextStudyAssignment,
	STUDY_ASSIGNMENT_LOADING_KEY,
	updateStudyAssignment,
} from 'src/features/study_assignment/store/studyAssignmentThunks';
import { AppDispatch, RootState } from 'src/store';
import { snakeToTitleCase } from 'src/utils/formatter';

const StudyAnnotationPage: React.FC = () => {
	const { siteSlug, studyId } = useParams<{
		siteSlug: string;
		studyId: string;
	}>();
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();

	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);
	const studyDetails = useSelector(
		(state: RootState) => state.study.studyDetails,
	);
	const loadingStates = useSelector(
		(state: RootState) => state.loading.loadingStates,
	);
	const isAnnotationLabelLoading =
		loadingStates[ANNOTATION_LABEL_LOADING_KEY];

	const [leftPanelOpen] = useState(true);
	const [rightPanelOpen] = useState(true);
	const [selectedImage, setSelectedImage] = useState<IStudyImageAsset | null>(
		null,
	);
	const [studyWithImages, setStudyWithImages] = useState<IStudyWithImages>();
	const [currentAnnotation, setCurrentAnnotation] =
		useState<IStudyAnnotation | null>(null);
	const [studyAssignmentDetails, setStudyAssignmentDetails] =
		useState<IGetCurrentUsersStudyAssignmentResponse | null>(null);
	const [imageAnnotations, setImageAnnotations] = useState<
		IStudyAnnotation[]
	>([]);
	const [annotationLabels, setAnnotationLabels] = useState<
		IAnnotationLabelShortInfo[]
	>([]);
	const [nextStudyAssignment, setNextStudyAssignment] =
		useState<IStudyWithAssignment | null>(null);
	const [isViewerLoading, setIsViewerLoading] = useState(false);
	const [isNextAssignmentLoading, setIsNextAssignmentLoading] =
		useState(false);
	const nextAssignmentCheckKey = useRef<string | null>(null);

	const isStudyAnnotationPageLoading =
		isViewerLoading ||
		isNextAssignmentLoading ||
		[
			STUDY_LOADING_KEY,
			STUDY_ASSIGNMENT_LOADING_KEY,
			QUESTIONNAIRE_LOADING_KEY,
			QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
			ANNOTATION_LABEL_LOADING_KEY,
		].some((loadingKey) => loadingStates[loadingKey]);

	const isAssignedUserViewing = !!studyAssignmentDetails;
	const isAdminUserViewing = siteDetails?.roles?.some((role) =>
		['super_admin', 'admin'].includes(role),
	);

	const breadcrumbs = [
		{
			label: siteDetails?.name || 'Site Details',
			href: `/site/${siteSlug}`,
		},
		{
			label: 'Study Assignments',
			href: `/site/${siteSlug}/study-assignments`,
		},
		{
			label:
				`${studyDetails?.patient_study_id} (${studyDetails?.study_name})` ||
				'Study ID',
		},
	];

	const disableAnnotation = useMemo(() => {
		return (
			!isAssignedUserViewing ||
			!currentAnnotation ||
			currentAnnotation.status === 'ungradable' ||
			!studyWithImages ||
			['ungradable', 'completed'].includes(
				studyAssignmentDetails.status,
			) ||
			isAnnotationLabelLoading
		);
	}, [
		currentAnnotation,
		studyWithImages,
		isAssignedUserViewing,
		studyAssignmentDetails,
	]);
	const hasStudyUpdateButtons = useMemo(() => {
		return (
			isAssignedUserViewing &&
			['ungradable', 'completed'].includes(studyAssignmentDetails.status)
		);
	}, [
		currentAnnotation,
		studyWithImages,
		isAssignedUserViewing,
		studyAssignmentDetails,
	]);

	const updateCurrentAnnotation = (data: IStudyAnnotation) => {
		setCurrentAnnotation(data);
	};

	const getNextAssignmentCheckKey = (
		assignmentDetails: IGetCurrentUsersStudyAssignmentResponse,
	) => `${assignmentDetails.id}:${assignmentDetails.status}`;

	const handleNextAssignmentClick = () => {
		if (!siteSlug || !nextStudyAssignment) return;

		navigate(
			`/site/${siteSlug}/patient/${nextStudyAssignment.patient_id}/study/${nextStudyAssignment.id}/study-annotation`,
		);
	};

	const fetchAnnotationLabels = () => {
		if (!siteSlug) return;
		dispatch(
			getAnnotationLabelsShortInfo({
				siteSlug,
				onSuccess: (data) => {
					setAnnotationLabels(data);
				},
			}),
		);
	};

	const fetchNextStudyAssignment = async (checkKey: string) => {
		if (!siteSlug) return;

		nextAssignmentCheckKey.current = checkKey;
		setIsNextAssignmentLoading(true);
		try {
			await dispatch(
				getNextStudyAssignment({
					siteSlug: siteSlug,
					onSuccess: (data) => {
						setNextStudyAssignment(data.next_study_assignment);
					},
					onError: () => {
						setNextStudyAssignment(null);
					},
				}),
			);
		} finally {
			setIsNextAssignmentLoading(false);
		}
	};

	const handleUpdateStudyAssignmentStatus = (
		status: StudyAssignmentStatus,
		onSuccess?: () => void,
	) => {
		if (!siteSlug || !studyAssignmentDetails?.id) return;

		dispatch(
			updateStudyAssignment({
				studyAssignmentId: studyAssignmentDetails.id,
				request: { status: status },
				siteSlug: siteSlug,
				onSuccess: (data) => {
					setStudyAssignmentDetails(data);
					setNextStudyAssignment(null);
					if (['completed', 'ungradable'].includes(status)) {
						fetchNextStudyAssignment(
							getNextAssignmentCheckKey(data),
						);
					}
					onSuccess?.();
				},
			}),
		);
	};

	useEffect(() => {
		if (!studyWithImages || !selectedImage?.url || !isAdminUserViewing)
			return;
		const image = studyWithImages.patient_assets.find(
			(asset) => asset.url === selectedImage.url,
		);

		if (image) setImageAnnotations(image?.annotations);

		return () => {
			setImageAnnotations([]);
		};
	}, [studyWithImages, selectedImage]);

	useEffect(() => {
		if (!selectedImage || !studyId || !siteSlug || isAdminUserViewing)
			return;
		if (
			!studyAssignmentDetails ||
			(studyAssignmentDetails &&
				studyAssignmentDetails.status === 'active')
		)
			return;

		dispatch(
			getStudyImageAnnotation({
				imageId: selectedImage.id,
				studyId,
				siteSlug,
				onSuccess: (data) => {
					setCurrentAnnotation(data);
				},
			}),
		);
	}, [selectedImage, studyAssignmentDetails]);

	useEffect(() => {
		if (!siteSlug || !studyId) return;

		let isActive = true;
		setIsViewerLoading(true);

		const fetchViewerData = async () => {
			const requests: Promise<unknown>[] = [
				Promise.resolve(
					dispatch(
						getStudyDetails({
							studyId: Number(studyId),
							siteSlug: siteSlug,
						}),
					),
				),

				Promise.resolve(
					dispatch(
						getStudyWithImages({
							studyId: Number(studyId),
							siteSlug: siteSlug,
							onSuccess: (response) => {
								if (!isActive) return;
								setStudyWithImages(response);
								const image = response?.patient_assets?.length
									? response.patient_assets[0]
									: null;

								setSelectedImage(image);
							},
						}),
					),
				),
			];

			if (!isAdminUserViewing) {
				requests.push(
					Promise.resolve(
						dispatch(
							getCurrentUsersStudyAssignment({
								studyId: Number(studyId),
								siteSlug: siteSlug,
								onSuccess: (data) => {
									if (!isActive) return;
									setStudyAssignmentDetails(data);
									if (
										['completed', 'ungradable'].includes(
											data.status,
										)
									) {
										fetchNextStudyAssignment(
											getNextAssignmentCheckKey(data),
										);
									}
								},
							}),
						),
					),
				);
			}

			await Promise.allSettled(requests);
			if (isActive) setIsViewerLoading(false);
		};

		fetchViewerData();

		return () => {
			isActive = false;
			setIsViewerLoading(false);
			setIsNextAssignmentLoading(false);
			setCurrentAnnotation(null);
			setImageAnnotations([]);
			setSelectedImage(null);
			setStudyWithImages(undefined);
			setStudyAssignmentDetails(null);
			setNextStudyAssignment(null);
			nextAssignmentCheckKey.current = null;
		};
	}, [siteSlug, studyId]);

	useEffect(() => {
		if (
			!studyAssignmentDetails ||
			!['completed', 'ungradable'].includes(studyAssignmentDetails.status)
		)
			return;

		const checkKey = getNextAssignmentCheckKey(studyAssignmentDetails);
		if (nextAssignmentCheckKey.current === checkKey) return;

		fetchNextStudyAssignment(checkKey);
	}, [studyAssignmentDetails, siteSlug]);
	useEffect(() => {
		fetchAnnotationLabels();
	}, []);

	return (
		<PageWithLayout
			breadcrumbs={breadcrumbs}
			headerCenterContent={
				<>
					{studyAssignmentDetails?.status && (
						<Box
							sx={{
								display: 'flex',
								flexShrink: 0,
								flex: 0.2,
								alignItems: 'flex-end',
								justifyContent: 'flex-end',
							}}
						>
							<Chip
								label={snakeToTitleCase(
									studyAssignmentDetails?.status,
								)}
								size='medium'
								variant='filled'
							/>
						</Box>
					)}
				</>
			}
			headerRightContent={
				<Box
					sx={{
						display: 'flex',
						flex: '0 0 auto',
						justifyContent: 'flex-end',
						marginLeft: 2,
						minWidth: 'max-content',
					}}
				>
					<StudyAssignmentActions
						studyAssignmentStatus={
							studyAssignmentDetails?.status || undefined
						}
						isAssignedUserViewing={!!isAssignedUserViewing}
						isAdminUserViewing={!!isAdminUserViewing}
						onUpdateStatus={handleUpdateStudyAssignmentStatus}
						hasNextAssignment={nextStudyAssignment !== null}
						onNextAssignment={handleNextAssignmentClick}
					/>
				</Box>
			}
		>
			<Box
				sx={{
					flex: 1,
					marginTop: '15px',
					borderRadius: '4px',
					position: 'relative',
					display: 'flex',
					flexDirection: 'row',
					height: hasStudyUpdateButtons
						? 'calc(100vh - 167px)'
						: 'calc(100vh - 175px)',
					boxSizing: 'border-box',
					width: '100%',
					minWidth: 0,
				}}
			>
				<ContainerLoadingIndicator
					loadingKey='study_annotation_page'
					manualLoading={isStudyAnnotationPageLoading}
				/>
				{/* Left Panel */}
				<Box
					sx={{
						width: leftPanelOpen ? '200px' : '40px',
						borderRadius: 2,
						boxShadow: 1,
						backgroundColor: 'white',
						display: 'flex',
						flexDirection: 'column',
						transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
						height: '100%',
						flexShrink: 0,
					}}
				>
					{/* Top: toggle button */}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							position: 'relative',
							p: 1,
							mt: 1,
						}}
					>
						{/* <IconButton
							onClick={() => setLeftPanelOpen(!leftPanelOpen)}
							size='small'
							sx={{
								position: leftPanelOpen ? 'absolute' : 'static',
								right: leftPanelOpen ? 4 : 'auto',
							}}
						>
							<StartIcon
								sx={{
									transform: leftPanelOpen
										? 'rotate(180deg)'
										: 'rotate(0deg)',
								}}
							/>
						</IconButton> */}

						{leftPanelOpen && (
							<Typography sx={{ textAlign: 'center' }}>
								Images
							</Typography>
						)}
					</Box>

					{leftPanelOpen && (
						<Box sx={{ flex: 1, p: 1, minHeight: 0 }}>
							{studyWithImages?.patient_assets?.length ? (
								<ImageThumbnails
									images={studyWithImages.patient_assets}
									selectedImageURL={
										selectedImage?.url ?? null
									}
									onSelectImage={(image) => {
										if (selectedImage?.id === image.id)
											return;
										setCurrentAnnotation(null);
										setSelectedImage(image);
									}}
								/>
							) : (
								<NoDataView message='No images available for this study' />
							)}
						</Box>
					)}
				</Box>

				{/* Center Panel */}
				<Box
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						mx: 2,
						minWidth: 0,
					}}
				>
					{selectedImage?.url && (
						<FabricImageAnnotator
							imageURL={selectedImage.url}
							currentAnnotation={currentAnnotation}
							assetAnnotations={imageAnnotations}
							onCurrentAnnotationUpdate={updateCurrentAnnotation}
							annotationLabels={annotationLabels}
							disabled={disableAnnotation}
						/>
					)}
				</Box>

				{/* Right Panel */}
				<Box
					sx={{
						width: rightPanelOpen ? '350px' : '40px',
						borderRadius: 2,
						boxShadow: 1,
						backgroundColor: 'white',
						display: 'flex',
						flexDirection: 'column',
						transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
						flexShrink: 0,
						height: '100%',
						overflow: 'hidden',
					}}
				>
					{/* Top: toggle button */}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							position: 'relative',
							p: 1,
							mt: 1,
						}}
					>
						{/* <IconButton
							onClick={() => setRightPanelOpen(!rightPanelOpen)}
							size='small'
							sx={{
								position: rightPanelOpen
									? 'absolute'
									: 'static',
								left: rightPanelOpen ? 4 : 'auto',
							}}
						>
							<StartIcon
								sx={{
									transform: rightPanelOpen
										? 'rotate(0deg)'
										: 'rotate(180deg)',
								}}
							/>
						</IconButton> */}

						{rightPanelOpen && (
							<Typography sx={{ textAlign: 'center' }}>
								Questionnaires
							</Typography>
						)}
					</Box>

					{rightPanelOpen && (
						<Box
							sx={{
								flex: 1,
								paddingX: 1,
								minHeight: 0,
								overflowY: 'auto',
								scrollbarWidth: 'thin',
							}}
						>
							<QuestionnairePanel />
						</Box>
					)}
				</Box>
			</Box>
		</PageWithLayout>
	);
};

export default StudyAnnotationPage;
